import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getPost } from '../../../models/postModel';
import { toast } from 'react-toastify';

const flattenPosts = (payload) => {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.rows)) {
    return payload.rows;
  }

  if (payload && typeof payload === 'object' && 'post_id' in payload) {
    return [payload];
  }

  if (payload?.post_categories && typeof payload.post_categories === 'object') {
    return Object.entries(payload.post_categories).reduce((acc, [categoryLabel, postsInCategory]) => {
      if (Array.isArray(postsInCategory)) {
        const mapped = postsInCategory.map((post) => ({
          ...post,
          category_name: post.category_name || categoryLabel,
        }));
        acc.push(...mapped);
      }
      return acc;
    }, []);
  }

  if (Array.isArray(payload?.notices)) {
    return payload.notices;
  }

  if (payload?.post) {
    return [payload.post];
  }

  return [];
};

function PostView() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (user && postId) {
      fetchPost();
    }
  }, [user, postId]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await getPost(postId);

      if (response.status === 1) {
        const normalizedPosts = flattenPosts(response.data || response.post || response);
        const matchedPost = normalizedPosts.find((item) => String(item.post_id) === String(postId));

        if (matchedPost) {
          setPost(matchedPost);
        } else if (response.post) {
          setPost(response.post);
        } else {
          throw new Error('Post not found in response');
        }
      } else {
        throw new Error(response.message || 'Failed to fetch post');
      }
    } catch (err) {
      console.error('Error fetching post:', err);
      setError(err.message);
      toast.error(err.message || 'Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusKey = typeof status === 'number'
      ? (status === 1 ? 'published' : status === 2 ? 'archived' : 'draft')
      : (status || 'draft');

    const statusConfig = {
      published: { color: 'bg-green-100 text-green-800', label: 'Published' },
      draft: { color: 'bg-yellow-100 text-yellow-800', label: 'Draft' },
      archived: { color: 'bg-gray-100 text-gray-800', label: 'Archived' }
    };
    
    const config = statusConfig[statusKey] || statusConfig.draft;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const hasContent = (text) => {
    return text && text.trim().length > 0 && text.trim() !== '<p></p>' && text.trim() !== '<p><br></p>';
  };

  if (loading) {
    return (
      <div className="p-4 py-6 md:p-8 flex justify-center items-center h-[calc(100vh-150px)]">
        <div 
          className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4" 
          style={{ borderColor: 'var(--color-secondary-default)' }}
        ></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 py-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-red-800">Error Loading Post</h3>
                <p className="text-red-700 mt-1">{error}</p>
              </div>
            </div>
            <div className="mt-6 flex space-x-3">
              <button
                onClick={fetchPost}
                className="custom-btn-secondary text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition duration-150 ease-in-out"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('..')}
                className="text-center py-2 px-4 rounded-lg custom-text-neutral-600 bg-neutral-200 hover:bg-neutral-300 transition"
              >
                Back to Posts
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="p-4 py-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-yellow-800">Post Not Found</h3>
                <p className="text-yellow-700 mt-1">The post you're looking for doesn't exist or has been removed.</p>
              </div>
            </div>
            <div className="mt-6">
              <button
                onClick={() => navigate('..')}
                className="text-center py-2 px-4 rounded-lg custom-text-neutral-600 bg-neutral-200 hover:bg-neutral-300 transition"
              >
                Back to Posts
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 py-6 md:p-8 space-y-6 md:space-y-8">
      <div className="max-w-7xl mx-auto">
        {/* Header with Navigation */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('..')}
            className="flex items-center space-x-2 text-neutral-600 hover:text-neutral-800 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">Back to Posts</span>
          </button>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(`edit`)}
              className="flex items-center space-x-2 custom-btn-secondary text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition duration-150 ease-in-out"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>Edit Post</span>
            </button>
          </div>
        </div>

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-8 md:p-12 mb-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-4 mb-6">
              {getStatusBadge(post.post_status)}
              <span className="text-neutral-600">•</span>
              <span className="text-neutral-600">{post.category_name || 'General'}</span>
              <span className="text-neutral-600">•</span>
              <span className="text-neutral-600">{post.views || 0} views</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold custom-text-neutral-800 mb-6 leading-tight">
              {post.post_title}
            </h1>
            
            <div className="flex items-center justify-center space-x-6 text-sm text-neutral-600">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>Created: {formatDate(post.created_at)}</span>
              </div>
              {post.updated_at && post.updated_at !== post.created_at && (
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Updated: {formatDate(post.updated_at)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Article Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Featured Image - Enhanced for image-focused posts */}
              {post.post_image && (
                <div className="relative bg-gradient-to-r from-blue-500 to-purple-600">
                  <div className="aspect-[4/3] md:aspect-[16/10] lg:aspect-[16/9] xl:aspect-[3/2] relative">
                    <img 
                      src={post.post_image} 
                      alt={post.post_title}
                      className={`w-full h-full object-contain transition-opacity duration-300 ${
                        imageLoaded ? 'opacity-100' : 'opacity-0'
                      }`}
                      onLoad={() => setImageLoaded(true)}
                      onError={(e) => {
                        // Hide the image if it fails to load
                        e.target.style.display = 'none';
                        // Show a fallback message
                        const fallback = document.createElement('div');
                        fallback.className = 'absolute inset-0 flex items-center justify-center text-white text-lg font-medium bg-gray-800';
                        fallback.innerHTML = `
                          <div class="text-center">
                            <svg class="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                            </svg>
                            <p class="text-gray-300">Image not available</p>
                          </div>
                        `;
                        e.target.parentNode.appendChild(fallback);
                      }}
                    />
                    {!imageLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div 
                          className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4" 
                          style={{ borderColor: 'var(--color-secondary-default)' }}
                        ></div>
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-black bg-opacity-10 pointer-events-none"></div>
                </div>
              )}
              
              {/* Article Body - Only show if there's actual content */}
              {hasContent(post.post_text) && (
                <div className="p-8">
                  <div className="prose prose-lg max-w-none">
                    <div 
                      className="text-neutral-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: post.post_text }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Post Details Card */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold custom-text-neutral-800 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Post Details
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-neutral-600">Post ID</span>
                  <span className="text-sm text-neutral-800 font-mono">#{post.post_id}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-neutral-600">Category</span>
                  <span className="text-sm text-neutral-800">{post.category_name || 'General'}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-neutral-600">Status</span>
                  {getStatusBadge(post.post_status)}
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-neutral-600">Views</span>
                  <span className="text-sm text-neutral-800">{post.views || 0}</span>
                </div>

                {post.post_image && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-neutral-600">Has Image</span>
                    <span className="text-sm text-green-600 font-medium">✓ Yes</span>
                  </div>
                )}

                {hasContent(post.post_text) && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-neutral-600">Has Content</span>
                    <span className="text-sm text-green-600 font-medium">✓ Yes</span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions Card */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold custom-text-neutral-800 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                </svg>
                Quick Actions
              </h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => navigate(`edit`)}
                  className="w-full flex items-center justify-center space-x-2 custom-btn-secondary text-white font-semibold py-2.5 px-4 rounded-lg shadow-md hover:shadow-lg transition duration-150 ease-in-out"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span>Edit Post</span>
                </button>
                
                <button
                  onClick={() => navigate('..')}
                  className="w-full flex items-center justify-center space-x-2 text-center py-2.5 px-4 rounded-lg custom-text-neutral-600 bg-neutral-200 hover:bg-neutral-300 transition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>View All Posts</span>
                </button>
              </div>
            </div>

            {/* Share Card */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold custom-text-neutral-800 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
                Share Post
              </h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success('Link copied to clipboard!');
                  }}
                  className="w-full flex items-center justify-center space-x-2 text-center py-2.5 px-4 rounded-lg custom-text-neutral-600 bg-neutral-200 hover:bg-neutral-300 transition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Copy Link</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PostView; 